import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, apiPostForm } from './client'

export const DIAGNOSTIC_PAGES = [
  'computer_scan',
  'network_scan',
  'security_scan',
  'skus_report',
  'general_settings',
] as const

export type DiagnosticReportPage = (typeof DIAGNOSTIC_PAGES)[number]

export function isDiagnosticReportPage(value: string): value is DiagnosticReportPage {
  return (DIAGNOSTIC_PAGES as readonly string[]).includes(value)
}

export type DiagnosticReportShell = {
  page: string
  pageTitle: string
  title: string
  company_id: number
  report_id: number
  moduleUrl: string
}

export type DiagnosticReportSettings = {
  company_id: number
  report_id: number
  report_folder: string
  logo_url: string
  logo_src: string
  default_settings: {
    report_title: string
    report_color: string
    company_phone: string
    report_logo: string
  }
  settings: {
    report_title: string
    report_color: string
    company_phone: string
    report_logo: string
  }
}

export type DiagnosticReportActions = {
  company_id: number
  report_id: number
  actions: Record<string, { values: unknown; checked: number | string }>
  ticket_types: Array<{ ID: number; name: string }>
  ticket_statuses: Record<string, string>
  selected_ticket_type: number
  selected_ticket_status: number
}

export function diagnosticPageQuery(page: DiagnosticReportPage) {
  return queryOptions({
    queryKey: ['settings', 'diagnostic_reports', page],
    queryFn: () => apiGet<DiagnosticReportShell>(`/settings/diagnostic_reports/${page}`),
  })
}

export function reportSettingsQuery(companyId: number, reportId: number) {
  return queryOptions({
    queryKey: ['settings', 'diagnostic_reports', 'settings', companyId, reportId],
    queryFn: () => apiGet<DiagnosticReportSettings>(`/settings/diagnostic_reports/${companyId}/${reportId}/settings`),
    enabled: companyId > 0 && reportId > 0,
  })
}

export function reportActionsQuery(companyId: number, reportId: number) {
  return queryOptions({
    queryKey: ['settings', 'diagnostic_reports', 'actions', companyId, reportId],
    queryFn: () => apiGet<DiagnosticReportActions>(`/settings/diagnostic_reports/${companyId}/${reportId}/actions`),
    enabled: companyId > 0 && reportId > 0,
  })
}

export function saveReportSettings(body: FormData) {
  return apiPostForm<{ ok?: boolean }>('/settings/diagnostic_reports/settings', body)
}

export function saveReportActions(body: FormData) {
  return apiPostForm<{ ok?: boolean }>('/settings/diagnostic_reports/actions', body)
}

export function ticketStatusesQuery(ticketType: number) {
  return queryOptions({
    queryKey: ['settings', 'diagnostic_reports', 'ticket-statuses', ticketType],
    queryFn: () => apiGet<Record<string, string>>(`/settings/diagnostic_reports/ticket-statuses?ticket_type=${ticketType}`),
    enabled: ticketType > 0,
  })
}

export function rebuildGeneralConfiguration() {
  return apiPost<Record<string, never>>('/settings/diagnostic_reports/rebuild_general_configuration', {})
}
